import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AlertTriangle, ChevronRight, MessageCircle, IndianRupee, Calendar } from "lucide-react";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20need%20help%20with%20GST%20interest%20liability";

const formatINR = (num) =>
  Math.round(num || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const todayStr = () => new Date().toISOString().slice(0, 10);

const GSTInterestCalculatorPage = () => {
  const [taxAmount, setTaxAmount] = useState(100000);
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [paidDate, setPaidDate] = useState(todayStr());

  useCanonical("/gst-interest-calculator");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "GST Interest Calculator",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "All",
    "description": "Calculate GST interest at 18% per annum on delayed GST payments as per Section 50 of CGST Act.",
    "url": "https://wintrustsolutions.com/gst-interest-calculator",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
  });

  const results = useMemo(() => {
    const tax = Math.max(0, Number(taxAmount) || 0);
    const due = new Date(dueDate);
    const paid = new Date(paidDate);

    if (isNaN(due.getTime()) || isNaN(paid.getTime())) {
      return { days: 0, interest: 0, total: tax };
    }

    const diffMs = paid.getTime() - due.getTime();
    const days = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));

    const interest = days === 0 ? 0 : (tax * 0.18 * days) / 365;
    const total = tax + interest;

    return { days, interest, total };
  }, [taxAmount, dueDate, paidDate]);

  return (
    <>
      <Helmet>
        <title>GST Interest Calculator — 18% Interest on Late Payment | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Calculate GST interest at 18% per annum on delayed GST payments instantly. Free online calculator per Section 50 of CGST Act. Expert help in Coimbatore." />
      </Helmet>

      <section className="bg-navy py-12 md:py-16">
        <div className="container-custom">
          <nav className="text-slate-400 text-sm mb-4">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">GST Interest Calculator</span>
          </nav>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-heading leading-tight mb-4" data-testid="gst-interest-h1">
            GST Interest Calculator — 18% Interest on Late Payment
          </h1>
          <p className="text-slate-300 text-lg max-w-3xl">
            Calculate interest payable on delayed GST payments as per Section 50 of the CGST Act, 2017. 18% per annum applied on outstanding tax for each day of delay.
          </p>
        </div>
      </section>

      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-10">
            {/* Inputs */}
            <div className="bg-white rounded-xl p-6 md:p-8 border border-slate-200 shadow-sm">
              <h2 className="text-2xl font-semibold text-navy mb-6 font-heading">Enter Details</h2>

              <div className="mb-6">
                <label className="text-navy font-semibold mb-2 flex items-center gap-2">
                  <IndianRupee size={16} /> Outstanding Tax Amount
                </label>
                <input
                  type="number"
                  min={0}
                  value={taxAmount}
                  onChange={(e) => setTaxAmount(Math.max(0, Number(e.target.value)))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy font-medium focus:outline-none focus:border-gold"
                  data-testid="tax-amount-input"
                />
              </div>

              <div className="mb-6">
                <label className="text-navy font-semibold mb-2 flex items-center gap-2">
                  <Calendar size={16} /> Payment Due Date
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy font-medium focus:outline-none focus:border-gold"
                  data-testid="due-date-input"
                />
              </div>

              <div>
                <label className="text-navy font-semibold mb-2 flex items-center gap-2">
                  <Calendar size={16} /> Actual Payment Date
                </label>
                <input
                  type="date"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy font-medium focus:outline-none focus:border-gold"
                  data-testid="paid-date-input"
                />
              </div>
            </div>

            {/* Results */}
            <div className="space-y-4" data-testid="gst-interest-results">
              <div className="bg-navy rounded-xl p-6 md:p-8 text-center">
                <p className="text-slate-400 text-sm mb-1">Total Amount (Tax + Interest)</p>
                <p className="text-4xl md:text-5xl font-bold text-gold font-heading" data-testid="total-amount">
                  ₹{formatINR(results.total)}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                  <p className="text-slate-500 text-xs mb-1">Days Delayed</p>
                  <p className="text-xl font-bold text-navy" data-testid="days-delayed">{results.days}</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                  <p className="text-slate-500 text-xs mb-1">Interest Payable</p>
                  <p className="text-xl font-bold text-navy" data-testid="interest-amount">₹{formatINR(results.interest)}</p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-5 border border-slate-200 text-xs text-slate-600">
                <strong className="text-navy">Formula:</strong> Interest = Tax × 18% × Days ÷ 365
              </div>
            </div>
          </div>

          {/* Amber Alert */}
          <div className="mt-10 bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6 md:p-8" data-testid="amber-alert">
            <div className="flex items-start gap-4">
              <AlertTriangle className="text-amber-600 flex-shrink-0 mt-1" size={28} />
              <div className="flex-1">
                <p className="text-amber-900 font-semibold text-base md:text-lg mb-4">
                  Delaying GST payment increases your interest daily. WhatsApp us now for immediate resolution support.
                </p>
                <div className="flex flex-wrap gap-3 items-center">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp"
                    data-testid="cta-whatsapp-btn"
                  >
                    <MessageCircle size={18} /> Resolve GST Issue — WhatsApp Us
                  </a>
                  <Link
                    to="/gst-return-filing"
                    className="text-navy font-semibold hover:text-gold transition-colors flex items-center gap-1"
                    data-testid="link-gst-filing"
                  >
                    View GST Return Filing Services <ChevronRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-8 text-xs text-slate-500 max-w-3xl">
            <strong>Disclaimer:</strong> Calculated at 18% per annum as per Section 50 of CGST Act, 2017. Consult a GST expert for case-specific advice.
          </p>
        </div>
      </section>
    </>
  );
};

export default GSTInterestCalculatorPage;
